import logService from '../wss/logService3';

/**
 * Generic LIFO stack cache.
 * Stores an ordered list of values, push/pop/peek only — no key or rk based
 * lookup (see CachedDictionary/CacheBase2 for that). Domain-specific
 * operations belong in subclasses.
 */
export default class CachedStack {
    _name = 'CachedStack';
    _data = [];

    /**
     * Guards against null _data. Logs the calling method name if triggered.
     */
    _check = (method) => {
        if (!this._data) {
            logService.logError(`${this._name}.${method}: _data is null`);
            this._data = [];
        }
    };

    /**
     * Pushes a value onto the top of the stack.
     */
    push = (value) => {
        try {
            this._check('push');
            this._data.push(value);
        } catch (error) {
            logService.logError(`${this._name}.push: ${error.message}`);
        }
    };

    /**
     * Removes and returns the top value, or null if the stack is empty.
     */
    pop = () => {
        try {
            this._check('pop');
            if (this._data.length === 0) {
                return null;
            }
            return this._data.pop();
        } catch (error) {
            logService.logError(`${this._name}.pop: ${error.message}`);
            return null;
        }
    };

    /**
     * Returns the top value without removing it, or null if the stack is empty.
     */
    peek = () => {
        try {
            this._check('peek');
            if (this._data.length === 0) {
                return null;
            }
            return this._data[this._data.length - 1];
        } catch (error) {
            logService.logError(`${this._name}.peek: ${error.message}`);
            return null;
        }
    };

    /**
     * Returns the number of values currently on the stack.
     */
    size = () => {
        try {
            this._check('size');
            return this._data.length;
        } catch (error) {
            logService.logError(`${this._name}.size: ${error.message}`);
            return 0;
        }
    };

    /**
     * Clears the stack.
     */
    clear = () => {
        this._data = [];
    };
}
